import classNames from "classnames";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import {
  FirmWorkStatus,
  SupportTicketRow,
  useGetDemosQuery,
  useGetSupportCountQuery,
  useGetSupportStatsQuery,
  useGetSupportStatusesQuery,
  useSetFirmWorkStatusMutation,
  useSetSupportStatusMutation,
  WorkStatusChoice,
} from "../../slices/SupportSlice";
import { NewEmailModal } from "./NewEmailModal";
import {
  caseStatusGroups,
  errorText,
  formatMinutes,
  parseStatusValue,
  PILL_CLASSES,
  statusBadge,
  statusValue,
  workChoiceValue,
  workStatusPillStyle,
} from "./support.utils";
import { PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

/** Help desk title, the two big sections (Support cases / New firms) and the shortcuts (owner 2026-10-25). */
export const HelpDeskHeader = ({
  section,
  subtitle,
}: {
  section: "cases" | "firms" | "demos" | "emails" | null;
  subtitle?: React.ReactNode;
}) => {
  const { data: count } = useGetSupportCountQuery(undefined, {
    pollingInterval: 120000,
  });
  const { data: stats } = useGetSupportStatsQuery();
  const { data: demos } = useGetDemosQuery();
  const upcomingDemos = (demos?.demos || []).filter(
    (d) => d.state === "booked",
  ).length;
  // "New email" (owner 2026-11-03) on the cases list and the Emails tab.
  const [composing, setComposing] = useState(false);
  const canCompose = section === "cases" || section === "emails";
  return (
    <div className={"mb-4"} data-testid={"helpdesk-header"}>
      <h1
        className={
          "mb-1 text-[26px] font-extrabold tracking-[-0.5px] text-[#1F2433]"
        }>
        Help desk
      </h1>
      {subtitle && (
        <div className={"text-[14px] text-[#5D6475]"}>{subtitle}</div>
      )}
      <div
        className={"mt-3.5 flex flex-wrap items-center justify-between gap-3"}>
        <div
          role={"tablist"}
          aria-label={"Help desk sections"}
          className={"inline-flex flex-wrap gap-1 rounded-xl bg-[#ECEEF3] p-1"}>
          <SectionTab
            to={"/support"}
            active={section === "cases"}
            label={"Support cases"}
            count={count?.needsYou ?? 0}
            strong
          />
          <SectionTab
            to={"/support/new-firms"}
            active={section === "firms"}
            label={"New firms"}
            count={stats?.newFirms ?? 0}
          />
          <SectionTab
            to={"/support/demos"}
            active={section === "demos"}
            label={"Demos"}
            count={upcomingDemos}
          />
          <SectionTab
            to={"/support/emails"}
            active={section === "emails"}
            label={"Emails"}
          />
        </div>
        <div className={"flex flex-wrap gap-2"}>
          {canCompose && (
            <button
              type={"button"}
              onClick={() => setComposing(true)}
              className={PRIMARY_BUTTON + " inline-flex items-center gap-1.5"}
              data-testid={"new-email-button"}>
              <span aria-hidden={"true"} className={"text-[16px] leading-none"}>
                +
              </span>
              New email
            </button>
          )}
          <Link
            to={"/support/saved-replies"}
            className={SMALL_BUTTON + " inline-flex items-center"}>
            Saved replies
          </Link>
          <Link
            to={"/support/automatic-emails"}
            className={SMALL_BUTTON + " inline-flex items-center"}>
            Automatic emails
          </Link>
          <Link
            to={"/support/settings"}
            title={"Help desk settings"}
            aria-label={"Help desk settings"}
            data-testid={"helpdesk-settings"}
            className={SMALL_BUTTON + " inline-flex items-center px-2.5"}>
            <GearIcon />
          </Link>
        </div>
      </div>
      {composing && <NewEmailModal onClose={() => setComposing(false)} />}
    </div>
  );
};

const SectionTab = ({
  to,
  active,
  label,
  count,
  strong,
}: {
  to: string;
  active: boolean;
  label: string;
  count?: number;
  strong?: boolean;
}) => (
  <Link
    to={to}
    role={"tab"}
    aria-selected={active}
    className={classNames(
      "inline-flex h-[38px] items-center gap-2 rounded-[9px] px-[18px] text-[14px]",
      active
        ? "bg-white font-bold text-[#1F2433] shadow-[0_1px_2px_rgba(20,20,40,.08)]"
        : "font-semibold text-[#3A4256] hover:text-[#1F2433]",
    )}>
    {label}
    {count !== undefined && (
      <span
        className={classNames(
          "rounded-full px-[7px] text-[12px]",
          strong && count > 0
            ? "bg-[#6B4EFF] text-white"
            : "bg-white text-[#3A4256]",
          active && !(strong && count > 0) && "bg-[#F1F2F5]",
        )}>
        {count}
      </span>
    )}
  </Link>
);

/** The four numbers at the top of the case list. */
export const HelpDeskStats = () => {
  const { data: stats } = useGetSupportStatsQuery(undefined, {
    pollingInterval: 120000,
  });
  const card = (
    title: string,
    value: React.ReactNode,
    note: React.ReactNode,
    to?: string,
  ) => {
    const body = (
      <>
        <div
          className={
            "text-[12px] font-bold uppercase tracking-[.5px] text-[#5D6475]"
          }>
          {title}
        </div>
        <div className={"mt-1.5 text-[26px] font-extrabold text-[#1F2433]"}>
          {value}
        </div>
        <div className={"text-[13px] text-[#5D6475]"}>{note}</div>
      </>
    );
    const cls =
      "block rounded-[14px] border border-[#EDEFF3] bg-white px-[18px] py-4";
    return to ? (
      <Link to={to} className={classNames(cls, "hover:border-[#D9D0FF]")}>
        {body}
      </Link>
    ) : (
      <div className={cls}>{body}</div>
    );
  };
  return (
    <div className={"mb-4 grid grid-cols-2 gap-3.5 lg:grid-cols-4"}>
      {card(
        "Open cases",
        stats?.open ?? "—",
        stats?.openUrgent ? `${stats.openUrgent} urgent` : "none urgent",
      )}
      {card(
        "Solved this week",
        stats?.solvedThisWeek ?? "—",
        stats && stats.ratedGood + stats.ratedBad > 0
          ? `${stats.ratedGood} rated good · ${stats.ratedBad} rated bad`
          : "no ratings yet",
      )}
      {card(
        "Avg. first reply",
        formatMinutes(stats?.avgFirstReplyMinutes),
        "this week",
      )}
      {card(
        "New firms in trial",
        stats?.newFirms ?? "—",
        <>
          {stats?.newFirmsNoClients
            ? `${stats.newFirmsNoClients} without clients yet · `
            : ""}
          <span className={"font-semibold text-[#6B4EFF]"}>Open ›</span>
        </>,
        "/support/new-firms",
      )}
    </div>
  );
};

const GearIcon = () => (
  <svg
    width={17}
    height={17}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={2}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}
    aria-hidden>
    <circle cx={12} cy={12} r={3} />
    <path
      d={
        "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
      }
    />
  </svg>
);

const PILL_SELECT =
  "max-w-full cursor-pointer appearance-none truncate rounded-full border-0 py-0.5 pl-2.5 pr-6 text-[12px] font-bold outline-none focus-visible:ring-2 focus-visible:ring-[#6B4EFF]/40 disabled:cursor-wait disabled:opacity-60";

/** A small down arrow over the right edge of a pill select. */
const PillArrow = () => (
  <svg
    width={10}
    height={10}
    viewBox={"0 0 10 10"}
    aria-hidden
    className={"pointer-events-none absolute right-2 top-1/2 -translate-y-1/2"}>
    <path
      d={"M2 3.5 5 6.5 8 3.5"}
      stroke={"currentColor"}
      strokeWidth={1.6}
      fill={"none"}
    />
  </svg>
);

/**
 * The status of a case as a colored dropdown (owner 2026-10-26): used in the case list and on the case page.
 * Main statuses with the owner's own statuses inside them; "Other mail" stays a plain pill (changed on the case page).
 */
export const CaseStatusSelect = ({
  ticket,
  label,
}: {
  ticket: Pick<
    SupportTicketRow,
    "id" | "status" | "customStatusId" | "statusName" | "statusColor"
  >;
  label?: string;
}) => {
  const { data: statuses } = useGetSupportStatusesQuery();
  const [setStatus, { isLoading }] = useSetSupportStatusMutation();
  const badge = statusBadge(ticket);
  if (ticket.status === "other")
    return (
      <span
        className={classNames(
          "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
          PILL_CLASSES[badge.tone],
        )}>
        {badge.text}
      </span>
    );
  const own = statuses?.caseStatuses || [];
  const value = statusValue(ticket);
  // A status the list doesn't know yet (just deleted, or still loading) keeps showing its name.
  const known =
    !ticket.customStatusId || own.some((s) => s.id === ticket.customStatusId);
  return (
    <span
      className={classNames(
        "relative inline-flex max-w-full rounded-full",
        PILL_CLASSES[badge.tone],
      )}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}>
      <select
        aria-label={label || "Status"}
        data-testid={"case-status-select"}
        value={value}
        disabled={isLoading}
        className={classNames(PILL_SELECT, "bg-transparent text-inherit")}
        onChange={(e) => {
          const next = parseStatusValue(e.target.value, own);
          if (!next) return;
          setStatus({ id: ticket.id, ...next })
            .unwrap()
            .catch((err) =>
              toast(errorText(err, "Couldn't change the status."), {
                type: "error",
              }),
            );
        }}>
        {ticket.status === "new" && <option value={"g:new"}>New</option>}
        {!known && <option value={value}>{badge.text}</option>}
        {caseStatusGroups(own).map((g) =>
          g.options.length === 1 ? (
            <option key={g.group} value={g.options[0].value}>
              {g.options[0].label}
            </option>
          ) : (
            <optgroup key={g.group} label={g.label}>
              {g.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </optgroup>
          ),
        )}
      </select>
      <PillArrow />
    </span>
  );
};

/**
 * New firms (owner 2026-10-26: "use the work item status"): the status of the firm's "Onboarding – …" work item, with
 * that work type's statuses; changing it here = changing it on the work item.
 */
export const FirmWorkStatusSelect = ({
  orgId,
  status,
  choices,
}: {
  orgId: string;
  status?: FirmWorkStatus | null;
  choices: WorkStatusChoice[];
}) => {
  const [setStatus, { isLoading }] = useSetFirmWorkStatusMutation();
  if (!status?.statusId) return null;
  const value = workChoiceValue(status.statusId, status.subStatusId);
  const known = choices.some(
    (c) => workChoiceValue(c.statusId, c.subStatusId) === value,
  );
  return (
    <span
      className={"relative inline-flex max-w-full rounded-full"}
      style={workStatusPillStyle(status.color)}>
      <select
        aria-label={"Onboarding work item status"}
        data-testid={"firm-work-status-select"}
        value={value}
        disabled={isLoading}
        className={classNames(PILL_SELECT, "bg-transparent text-inherit")}
        onChange={(e) => {
          const [statusId, subStatusId] = e.target.value.split(":");
          setStatus({ orgId, statusId, subStatusId: subStatusId || null })
            .unwrap()
            .catch((err) =>
              toast(errorText(err, "Couldn't change the status."), {
                type: "error",
              }),
            );
        }}>
        {!known && <option value={value}>{status.label}</option>}
        {choices.map((c) => (
          <option
            key={workChoiceValue(c.statusId, c.subStatusId)}
            value={workChoiceValue(c.statusId, c.subStatusId)}>
            {c.label}
          </option>
        ))}
      </select>
      <PillArrow />
    </span>
  );
};
